import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
  useGetWarehouseAnalyticsQuery,
  useGetTopCustomersQuery,
  useGetTrendingProductsQuery,
  useGetBestSellersQuery,
} from '#services/analytics';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { formatCurrency, getDateOnly, addDaysInTZ } from '#common/utils';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { FeedbackUIContext } from '#context/feedback-ui-context';

// Highcharts 13 defaults palette.colorScheme to 'light dark' and follows the OS
// theme. The admin surfaces are light-only, so pin the charts to light.
Highcharts.setOptions({ palette: { colorScheme: 'light' } });

// Chart labels and tooltips are rendered with useHTML, so any value that
// originates from the data has to be escaped before it goes into the markup.
const escHtml = (value: unknown) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// Trending Products' collection filter is a per-user view preference, so it
// lives in localStorage rather than on the server. Reads and writes are guarded
// because localStorage throws in private-mode / blocked-cookie browsers.
const EXCLUDED_COLLECTIONS_KEY = 'warehouseAnalytics.trendingExcludedCollections';

const readExcludedCollections = (): string[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(EXCLUDED_COLLECTIONS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const writeExcludedCollections = (values: string[]) => {
  try {
    localStorage.setItem(EXCLUDED_COLLECTIONS_KEY, JSON.stringify(values));
  } catch {
    // Preference is best-effort; a blocked localStorage just means it resets.
  }
};

interface MonthStat {
  month: string;
  totalPrice: number;
}

interface ComparisonStats {
  thisMonth?: { count: number; revenue: number };
  prevMonthSamePeriod?: { count: number; revenue: number };
  lastYearYtdRevenue?: number;
}

interface OrderItemCopy {
  collectionNames: string[];
  amounts: number;
  unitPrice: number;
}

interface OrderData {
  itemsCopy: OrderItemCopy[];
}

interface AnalyticsData {
  ordersStatisticsLineChartData: MonthStat[];
  comparisonStats?: ComparisonStats;
  ordersData: OrderData[];
}

interface TopCustomerOrder {
  createdAsUserId: number | string;
  createdAsUserName: string;
  createdAsUserCode: string | number;
  totalPrice: number;
}

interface TrendingProduct {
  productId: number | string;
  name: string;
  currentUnits: number;
  priorUnits: number;
  isNew: boolean;
  growthPct: number | null;
}

interface TrendingProductsResponse {
  products: TrendingProduct[];
  // Every collection seen in the window, excluded ones included, so the picker
  // can still offer a collection that is currently filtered out.
  collections: string[];
}

interface BestSeller {
  label: string;
  amounts: number;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  size: number;
}

interface DashCard {
  title: string;
  value: number;
  compValue: number;
  compLabel: string;
  trend: { pct: number; isUp: boolean } | null;
  format: (v: number) => string;
  accentColor: string;
  milestone?: MilestoneTier | null;
}

// Permissive view of the various Highcharts formatter `this` contexts. The full
// options objects are cast to Highcharts.Options on the way into <HighchartsReact>.
interface HCContext {
  value: number;
  y: number;
  x: number;
  pos: number;
  index: number;
  point: { name?: string; [key: string]: unknown };
  points: Array<{ y: number; category: unknown; series: { name: string }; point: { index: number; [key: string]: unknown }; [key: string]: unknown }>;
  series: { name: string };
  [key: string]: unknown;
}

interface MilestoneTier {
  threshold: number;
  storageKey: string;
  banner: string;
  emojis: string;
  gradient: string;
  animated: boolean;
  glow: string;
  borderColor: string;
  titleColor: string;
  valueColor: string;
  bannerColor: string;
  contentBg: string;
  confetti: string[];
  confettiScale: number;
}

// Highest threshold first - getMilestone() takes the first match.
const MILESTONE_TIERS: MilestoneTier[] = [
  {
    threshold: 3000000,
    storageKey: 'revenueMilestone3MDate',
    banner: '\uD83D\uDC51 TRIPLE MILLION LEGEND! \uD83D\uDC51',
    emojis: '\uD83D\uDC51\uD83D\uDE80\uD83D\uDC8E',
    gradient: 'linear-gradient(120deg, #00E5A0 0%, #00B4D8 25%, #7B2FF7 50%, #FF2E93 75%, #FFD700 100%)',
    animated: true,
    glow: '0 12px 44px rgba(123, 47, 247, 0.5)',
    borderColor: '#FF2E93',
    titleColor: '#4A0E5C',
    valueColor: '#7B2FF7',
    bannerColor: '#FF2E93',
    contentBg: 'rgba(255, 255, 255, 0.9)',
    confetti: ['#00E5A0', '#00B4D8', '#7B2FF7', '#FF2E93', '#FFD700'],
    confettiScale: 1.8,
  },
  {
    threshold: 2000000,
    storageKey: 'revenueMilestone2MDate',
    banner: '\uD83D\uDC8E DOUBLE MILLION CLUB! \uD83D\uDC8E',
    emojis: '\uD83C\uDFC6\uD83D\uDC8E\u2728',
    gradient: 'linear-gradient(135deg, #7F5AF0 0%, #2CB6E4 45%, #E6E8F0 100%)',
    animated: true,
    glow: '0 10px 36px rgba(127, 90, 240, 0.45)',
    borderColor: '#9B7BFF',
    titleColor: '#3B2A8C',
    valueColor: '#5B3BD6',
    contentBg: 'rgba(255, 255, 255, 0.92)',
    confetti: ['#7F5AF0', '#2CB6E4', '#E6E8F0', '#B388FF', '#00D4FF'],
    confettiScale: 1.4,
    bannerColor: '#2CB6E4',
  },
  {
    threshold: 1000000,
    // Legacy key: keeps installs that already celebrated 1M from re-firing.
    storageKey: 'millionaireMilestoneDate',
    banner: '\uD83C\uDF1F MILLIONAIRE MILESTONE! \uD83C\uDF1F',
    emojis: '\uD83C\uDF89\uD83D\uDCB0\uD83C\uDF8A',
    gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%)',
    animated: false,
    glow: '0 8px 32px rgba(255, 215, 0, 0.4)',
    borderColor: '#FFD700',
    titleColor: '#8B4513',
    valueColor: '#B8860B',
    bannerColor: '#FF6347',
    contentBg: 'rgba(255, 255, 255, 0.95)',
    confetti: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A'],
    confettiScale: 1,
  },
];

const getMilestone = (revenue: number) => MILESTONE_TIERS.find((t) => revenue >= t.threshold) || null;

export default function WarehouseAnalyticsDashboard() {
  const navigate = useNavigate();
  const marketTimeZone = useSelector(
    (state: { appConfig: { timeZone?: string } }) => state.appConfig?.timeZone,
  );
  const { showBackdrop, hideBackdrop } = useContext(FeedbackUIContext)!;
  const overlayActiveRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationShown, setCelebrationShown] = useState(false);

  // Inject CSS for hover effects once on component mount
  useEffect(() => {
    const styleId = 'warehouse-analytics-hover-style';

    // Check if style already exists to prevent duplicates
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .axis-label-hover {
          display: inline-block;
          padding: 2px 4px;
        }
        .axis-label-hover:hover {
          background-color: rgba(0,0,0,0.05);
          border-radius: 4px;
        }
      `;
      document.head.appendChild(style);

      // Cleanup: remove the style when component unmounts
      return () => {
        const styleElement = document.getElementById(styleId);
        if (styleElement) {
          styleElement.remove();
        }
      };
    }
    return undefined;
  }, []);

  // Move helper functions to component level
  const formatShortCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  const formatShortNumber = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };

  // Access control is handled by route permissions (ADMIN_ONLY)

  const { data: rawData, isLoading: analyticsLoading } = useGetWarehouseAnalyticsQuery(undefined);
  const data = rawData as AnalyticsData | undefined;
  // Shared date range driving both Top Customers and Trending Products.
  const [rangeDays, setRangeDays] = useState(30);
  const { data: rawTopCustomers, isLoading: topCustomersLoading } = useGetTopCustomersQuery(rangeDays);
  const topCustomersRaw = rawTopCustomers as TopCustomerOrder[] | undefined;
  const [excludedCollections, setExcludedCollections] = useState<string[]>(readExcludedCollections);
  const {
    data: rawTrendingProducts,
    isLoading: trendingProductsLoading,
  } = useGetTrendingProductsQuery({ daysBack: rangeDays, excludeCollections: excludedCollections });
  const trendingProductsRaw = rawTrendingProducts as TrendingProductsResponse | undefined;
  const { data: rawBestSellers, isLoading: bestSellersLoading } = useGetBestSellersQuery(rangeDays);
  const bestSellersRaw = rawBestSellers as BestSeller[] | undefined;

  useEffect(() => {
    const isInitialLoading = !hasLoadedRef.current
      && (analyticsLoading || topCustomersLoading || trendingProductsLoading || bestSellersLoading);
    if (isInitialLoading && !overlayActiveRef.current) {
      showBackdrop();
      overlayActiveRef.current = true;
    } else if (!isInitialLoading && overlayActiveRef.current) {
      hideBackdrop();
      overlayActiveRef.current = false;
    }
    if (!analyticsLoading && !topCustomersLoading && !trendingProductsLoading && !bestSellersLoading) {
      hasLoadedRef.current = true;
    }
    return () => {
      if (overlayActiveRef.current) {
        hideBackdrop();
        overlayActiveRef.current = false;
      }
    };
  }, [analyticsLoading, topCustomersLoading, trendingProductsLoading, bestSellersLoading, showBackdrop, hideBackdrop]);

  const currentYearRevenue = data?.ordersStatisticsLineChartData
    ?.filter(item => {
      const [year] = item.month.split('-');
      return year === new Date().getFullYear().toString();
    })
    ?.reduce((acc, curr) => acc + curr.totalPrice, 0) || 0;
  const milestone = getMilestone(currentYearRevenue);

  // Celebrate each revenue milestone: once on first hit, then for 2 more days.
  useEffect(() => {
    if (!milestone || celebrationShown) return;

    const storedDate = localStorage.getItem(milestone.storageKey);
    const now = new Date();

    if (!storedDate) {
      localStorage.setItem(milestone.storageKey, now.toISOString());
      setShowCelebration(true);
    } else {
      const daysDifference = (now.getTime() - new Date(storedDate).getTime()) / (1000 * 60 * 60 * 24);
      if (daysDifference <= 2) setShowCelebration(true);
    }
    setCelebrationShown(true);
  }, [milestone, celebrationShown]);

  // Confetti animation component - optimized for performance
  const ConfettiAnimation = () => {
    const [particles, setParticles] = useState<Particle[]>([]);

    useEffect(() => {
      if (showCelebration) {
        // Detect device performance capability
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isLowPerformance = isMobile || navigator.hardwareConcurrency <= 2;

        // Adjust particle count based on device capability
        const particleCount = Math.round((isLowPerformance ? 20 : 35) * (milestone?.confettiScale ?? 1));
        const animationDuration = isLowPerformance ? 3000 : 4000;

        // Create confetti particles
        const confettiColors = milestone?.confetti ?? ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A'];
        const newParticles: Particle[] = [];
        for (let i = 0; i < particleCount; i++) {
          newParticles.push({
            id: i,
            x: Math.random() * window.innerWidth,
            y: -10,
            vx: (Math.random() - 0.5) * 4,
            vy: Math.random() * 3 + 2,
            rotation: Math.random() * 360,
            rotationSpeed: (Math.random() - 0.5) * 10,
            color: confettiColors[Math.floor(Math.random() * confettiColors.length)]!,
            size: Math.random() * 6 + 3, // Slightly smaller particles
          });
        }
        setParticles(newParticles);

        let animationId: number;
        let lastTime = 0;

        // Use requestAnimationFrame for smoother, more efficient animation
        const animate = (currentTime: number) => {
          if (currentTime - lastTime >= 32) { // ~30fps instead of 60fps for better performance
            setParticles(prevParticles =>
              prevParticles.map(particle => ({
                ...particle,
                x: particle.x + particle.vx,
                y: particle.y + particle.vy,
                rotation: particle.rotation + particle.rotationSpeed,
                vy: particle.vy + 0.08, // Slightly reduced gravity for smoother motion
              })).filter(particle => particle.y < window.innerHeight + 20)
            );
            lastTime = currentTime;
          }

          if (currentTime < startTime + animationDuration) {
            animationId = requestAnimationFrame(animate);
          } else {
            setShowCelebration(false);
            setParticles([]);
          }
        };

        const startTime = performance.now();
        animationId = requestAnimationFrame(animate);

        return () => {
          if (animationId) {
            cancelAnimationFrame(animationId);
          }
        };
      }
      return undefined;
    }, [showCelebration]);

    if (!showCelebration || particles.length === 0) return null;

    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 9999,
          overflow: 'hidden',
          // Use GPU acceleration
          transform: 'translateZ(0)',
          willChange: 'transform',
        }}
      >
        {particles.map(particle => (
          <Box
            key={particle.id}
            sx={{
              position: 'absolute',
              left: particle.x,
              top: particle.y,
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              transform: `translate3d(0, 0, 0) rotate(${particle.rotation}deg)`, // GPU acceleration
              borderRadius: '2px',
              willChange: 'transform', // Optimize for animations
            }}
          />
        ))}
      </Box>
    );
  };

  const cardsGallery = () => {
    const cs = data?.comparisonStats;
    const thisMonth = cs?.thisMonth || { count: 0, revenue: 0 };
    const prev = cs?.prevMonthSamePeriod || { count: 0, revenue: 0 };
    const lastYearYtd = cs?.lastYearYtdRevenue || 0;

    const thisMonthAov = thisMonth.count > 0 ? thisMonth.revenue / thisMonth.count : 0;
    const prevAov = prev.count > 0 ? prev.revenue / prev.count : 0;

    const calcTrend = (current: number, previous: number) => {
      if (!previous || previous === 0) return null;
      const pct = Math.round(((current - previous) / previous) * 100);
      return { pct, isUp: pct >= 0 };
    };

    const now = new Date();
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthLabel = prevMonthDate.toLocaleString('default', { month: 'short' });

    const cards: DashCard[] = [
      {
        title: 'Orders This Month',
        value: thisMonth.count,
        compValue: prev.count,
        compLabel: `vs ${prevMonthLabel} same period`,
        trend: calcTrend(thisMonth.count, prev.count),
        format: (v) => String(v),
        accentColor: '#2196f3',
      },
      {
        title: 'Revenue This Month',
        value: thisMonth.revenue,
        compValue: prev.revenue,
        compLabel: `vs ${prevMonthLabel} same period`,
        trend: calcTrend(thisMonth.revenue, prev.revenue),
        format: formatShortCurrency,
        accentColor: '#4caf50',
      },
      {
        title: 'Avg Order Value',
        value: thisMonthAov,
        compValue: prevAov,
        compLabel: `vs ${prevMonthLabel} same period`,
        trend: calcTrend(thisMonthAov, prevAov),
        format: formatShortCurrency,
        accentColor: '#ff9800',
      },
      {
        title: 'Year to Date Revenue',
        value: currentYearRevenue,
        compValue: lastYearYtd,
        compLabel: 'vs last year same period',
        trend: calcTrend(currentYearRevenue, lastYearYtd),
        format: formatShortCurrency,
        accentColor: '#9c27b0',
        milestone,
      },
    ];

    return (
      <Box
        sx={{
          display: "flex",
          gap: 2
        }}>
        {cards.map((card) => (
          <Card
            key={card.title}
            sx={{
              width: 260,
              borderLeft: `4px solid ${card.accentColor}`,
              boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
              ...(card.milestone && {
                position: 'relative',
                background: card.milestone.gradient,
                backgroundSize: card.milestone.animated ? '300% 300%' : undefined,
                boxShadow: card.milestone.glow,
                border: `2px solid ${card.milestone.borderColor}`,
                animation: card.milestone.animated
                  ? 'gentlePulse 3s ease-in-out infinite, milestoneShift 8s ease infinite'
                  : 'gentlePulse 3s ease-in-out infinite',
                transform: 'translateZ(0)',
                willChange: 'transform',
                '@keyframes gentlePulse': {
                  '0%, 100%': { transform: 'translateZ(0) scale(1)' },
                  '50%': { transform: 'translateZ(0) scale(1.01)' },
                },
                '@keyframes milestoneShift': {
                  '0%, 100%': { backgroundPosition: '0% 50%' },
                  '50%': { backgroundPosition: '100% 50%' },
                },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }),
            }}
          >
            <CardContent sx={{
              ...(card.milestone && {
                position: 'relative',
                overflow: 'hidden',
                background: card.milestone.contentBg,
                ...(card.milestone.animated && {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.75) 50%, transparent 60%)',
                    transform: 'translateX(-100%)',
                    animation: 'milestoneShimmer 4s ease-in-out infinite',
                    pointerEvents: 'none',
                  },
                  '@keyframes milestoneShimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '60%, 100%': { transform: 'translateX(100%)' },
                  },
                  '@media (prefers-reduced-motion: reduce)': { '&::after': { animation: 'none', opacity: 0 } },
                }),
              }),
            }}>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  ...(card.milestone && { color: card.milestone.titleColor })
                }}>
                {card.title}
                {card.milestone && (
                  <Typography component="span" sx={{
                    ml: 1,
                    fontSize: '1.1em',
                    animation: 'gentleBounce 2s ease-in-out infinite',
                    '@keyframes gentleBounce': {
                      '0%, 100%': { transform: 'translateY(0)' },
                      '50%': { transform: 'translateY(-2px)' },
                    },
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }}>
                    {card.milestone.emojis}
                  </Typography>
                )}
              </Typography>
              <Typography variant="h4" sx={{
                fontWeight: 'bold',
                my: 1,
                color: card.milestone ? card.milestone.valueColor : 'text.primary',
              }}>
                {card.format(card.value)}
              </Typography>
              {card.milestone && (
                <Typography variant="caption" sx={{
                  display: 'block',
                  fontWeight: 'bold',
                  letterSpacing: '0.03em',
                  color: card.milestone.bannerColor,
                  mb: 0.5,
                }}>
                  {card.milestone.banner}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexWrap: "wrap"
                }}>
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  {card.compLabel}: {card.format(card.compValue)}
                </Typography>
                {card.trend && (
                  <Typography variant="caption" sx={{
                    fontWeight: 'bold',
                    color: card.trend.isUp ? 'success.main' : 'error.main',
                  }}>
                    {card.trend.isUp ? '▲' : '▼'} {card.trend.isUp ? '+' : ''}{card.trend.pct}%
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  const lineChartRevenue = () => {
    const NAVY = '#32668a';
    const GOLD = '#d9a258';
    const FORECAST_ORANGE = '#ff9800';
    const SERIES_TRAILING = 'Trailing 12 mo. revenue';
    const SERIES_CURRENT = 'Month, current year';
    const SERIES_PREVIOUS = 'Same month, previous year';
    const SERIES_FORECAST = 'Forecast';

    // Get current year data (last 12 months) and their months
    const allMonths = data?.ordersStatisticsLineChartData || [];
    const currentMonths = allMonths.slice(-12);

    const currentYearData = currentMonths.map(item => item.totalPrice);
    const months = currentMonths.map(item => item.month);

    const previousYearData = months.map(month => {
      // Extract year and month from current month
      const [currentYear, currentMonth] = month.split('-') as [string, string];
      // Construct previous year's month string
      const previousYearMonth = `${parseInt(currentYear) - 1}-${currentMonth}`;

      // Find matching data from previous year
      const previousData = allMonths.find(item => item.month === previousYearMonth);

      return previousData ? previousData.totalPrice : null;
    });

    // Trailing 12-month revenue: every point is a full year of revenue, so each
    // one already contains one of every season and the line only bends when the
    // business actually changes. Months with no orders are absent from the
    // aggregation, so sum by month key rather than by array position.
    const totalsByMonth = new Map(allMonths.map(item => [item.month, item.totalPrice]));
    const earliestMonth = allMonths[0]?.month;
    const shiftMonth = (monthKey: string, delta: number) => {
      const [year, month] = monthKey.split('-').map(Number) as [number, number];
      const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
      return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
    };
    const trailingTotal = (endMonth: string) => {
      // Incomplete window would read as a dip, so show nothing instead.
      if (!earliestMonth || shiftMonth(endMonth, -11) < earliestMonth) { return null; }
      let sum = 0;
      for (let back = 0; back < 12; back += 1) {
        sum += totalsByMonth.get(shiftMonth(endMonth, -back)) || 0;
      }
      return sum;
    };
        // Calculate forecast for current month
    const calculateCurrentMonthForecast = () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
      const currentMonthKey = `${currentYear}-${currentMonth}`;

      const currentDay = now.getDate();
      const daysInMonth = new Date(currentYear, now.getMonth() + 1, 0).getDate();

      if (currentDay >= daysInMonth) { return null; }

      // 1. Get current month's actual revenue so far from the full dataset
      const currentMonthDataObject = allMonths.find(item => item.month === currentMonthKey);
      const currentMonthRevenue = currentMonthDataObject ? currentMonthDataObject.totalPrice : 0;

      if (currentMonthRevenue === 0 && currentDay > 5) { return null; } // No forecast if no revenue past day 5

      const monthProgress = currentDay / daysInMonth;

      // 2. Calculate the average daily revenue for the current month
      const averageDailyRevenue = currentDay > 0 ? currentMonthRevenue / currentDay : 0;
      let adjustedDailyRevenue = averageDailyRevenue;

      // 3. Find last complete month's data to create a blended, more stable daily average
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthKey = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;
      const lastCompleteMonthDataObject = allMonths.find(item => item.month === lastMonthKey);

      if (lastCompleteMonthDataObject) {
        const daysInLastMonth = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth() + 1, 0).getDate();
        const lastMonthDailyAverage = daysInLastMonth > 0 ? lastCompleteMonthDataObject.totalPrice / daysInLastMonth : 0;

        if (lastMonthDailyAverage > 0) {
          // Dynamically adjust blending weights for the first 7 days of the month for stability
          const rampUpDays = 7;
          let currentMonthWeight = 0.7; // Default weight after ramp-up period

          if (currentDay <= rampUpDays) {
            // Linearly ramp up the weight for the current month's data from 10% to 70%
            currentMonthWeight = 0.1 + (0.6 * (currentDay / rampUpDays));
          }

          const lastMonthWeight = 1.0 - currentMonthWeight;

          // Blend the daily averages with the new dynamic weights
          adjustedDailyRevenue = (averageDailyRevenue * currentMonthWeight) + (lastMonthDailyAverage * lastMonthWeight);
        }
      }

      // 4. Project revenue for the rest of the month and add it to what's already earned
      const remainingDays = daysInMonth - currentDay;
      const finalProjectedRevenue = currentMonthRevenue + (adjustedDailyRevenue * remainingDays);

      // 5. Final safety check: ensure forecast is never less than what's already earned
      const finalForecast = Math.max(finalProjectedRevenue, currentMonthRevenue);

      return {
        month: currentMonthKey,
        value: finalForecast,
        isProjected: true,
        monthProgress: monthProgress
      };
    };

    const forecast = calculateCurrentMonthForecast();

    // A window ending on the open month is short by the unelapsed days and would
    // droop, so close that month with the forecast instead of leaving a gap. On
    // the final day of the month there is no forecast and none is needed.
    const today = new Date();
    const isMonthOver = today.getDate() >= new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const openMonth = isMonthOver
      ? null
      : `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const projectedTrailing = (endMonth: string) => {
      const actual = trailingTotal(endMonth);
      if (actual === null || !forecast) { return null; }
      return actual - (totalsByMonth.get(forecast.month) || 0) + forecast.value;
    };
    const trailingAt = (month: string) => (month === openMonth ? projectedTrailing(month) : trailingTotal(month));
    const trailingData = months.map(trailingAt);
    const latestClosedMonth = months.filter(month => month !== openMonth).pop();
    const trailingNow = latestClosedMonth ? trailingTotal(latestClosedMonth) : null;
    const trailingPrior = latestClosedMonth ? trailingTotal(shiftMonth(latestClosedMonth, -12)) : null;
    const trailingPct = trailingNow && trailingPrior
      ? Math.round(((trailingNow - trailingPrior) / trailingPrior) * 100)
      : null;

    // Prepare data arrays - keep original data intact
    const extendedMonths = [...months];
    const extendedCurrentYearData: Array<number | null> = [...currentYearData];
    const extendedPreviousYearData: Array<number | null> = [...previousYearData];
    const extendedTrailingData: Array<number | null> = [...trailingData];
    let forecastData: Array<number | null> = [];

    if (forecast) {
      // Check if current month already exists in the data
      const currentMonthIndex = extendedMonths.findIndex(month => month === forecast.month);

      if (currentMonthIndex >= 0) {
        // Stacked on top of the month's actual bar, so carry only the unearned
        // remainder; the two segments together stand at the forecast total.
        const earned = totalsByMonth.get(forecast.month) || 0;
        forecastData = extendedMonths.map(month => (month === forecast.month ? forecast.value - earned : null));
      } else {
        // Current month doesn't exist - add it to all arrays
        extendedMonths.push(forecast.month);
        extendedCurrentYearData.push(null); // No actual data for current month
        // The month is absent because it has no orders yet, which says nothing
        // about the same month last year — that bar still has a value to show.
        extendedPreviousYearData.push(totalsByMonth.get(shiftMonth(forecast.month, -12)) ?? null);
        extendedTrailingData.push(trailingAt(forecast.month)); // Closed with the forecast
        forecastData = extendedMonths.map(() => null);
        forecastData[forecastData.length - 1] = forecast.value; // Only forecast has value
      }
    }

    const projectedIndex = openMonth ? extendedMonths.indexOf(openMonth) : -1;
    // The forecast series plots a remainder; the tooltip wants the whole month.
    const forecastTotal = forecast ? forecast.value : null;

    const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const shortMonth = (monthKey: string) => MONTH_LABELS[parseInt(monthKey.split('-')[1] || '', 10) - 1] || monthKey;
    // Months with no orders are missing from the aggregation, so the window can
    // span more than a year and repeat a month name. Add the year when it does.
    const hasDuplicateLabels = new Set(extendedMonths.map(shortMonth)).size !== extendedMonths.length;
    const monthLabel = (monthKey: string) => (hasDuplicateLabels
      ? `${shortMonth(monthKey)} '${monthKey.slice(2, 4)}`
      : shortMonth(monthKey));

    const options = {
      chart: {
        type: 'column',
        marginTop: 50
      },
      title: {
        text: null
      },
      caption: {
        text: 'Monthly totals are grouped by UTC date boundaries.',
        align: 'center',
        style: {
          color: '#666',
          fontStyle: 'italic',
          fontSize: '12px'
        }
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: extendedMonths.map(monthLabel),
        crosshair: {
          width: 1,
          color: 'gray'
        }
      },
      yAxis: [{
        title: {
          text: 'Trailing 12 months',
          style: { color: NAVY }
        },
        labels: {
          style: { color: NAVY, fontWeight: '500' },
          formatter: function(this: HCContext) {
            return formatShortCurrency(this.value);
          }
        }
      }, {
        title: {
          text: 'Month',
          style: { color: GOLD }
        },
        labels: {
          style: { color: GOLD },
          formatter: function(this: HCContext) {
            return formatShortCurrency(this.value);
          }
        },
        opposite: true,
        // Default true stacks the first series on top; the earned revenue has
        // to sit at the base with the forecast riding above it.
        reversedStacks: false
      }],
      plotOptions: {
        column: {
          borderWidth: 0,
          groupPadding: 0.22,
          pointPadding: 0.05,
          // Two stacks, not three bars: the forecast sits on top of the current
          // month's actual instead of standing beside it.
          stacking: 'normal'
        }
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom'
      },
      series: ([{
        // Previous year first so each pair reads oldest-left, newest-right.
        name: SERIES_PREVIOUS,
        type: 'column',
        yAxis: 1,
        stack: 'previous',
        data: extendedPreviousYearData,
        color: 'rgba(217, 162, 88, 0.16)'
      }, {
        name: SERIES_CURRENT,
        type: 'column',
        yAxis: 1,
        stack: 'current',
        data: extendedCurrentYearData,
        color: 'rgba(217, 162, 88, 0.4)'
      }] as Array<Record<string, unknown>>).concat(forecast ? [{
        name: SERIES_FORECAST,
        type: 'column',
        yAxis: 1,
        stack: 'current',
        data: forecastData,
        color: 'rgba(255, 152, 0, 0.18)',
        borderColor: FORECAST_ORANGE,
        borderWidth: 1,
        dashStyle: 'ShortDash'
      }] : []).concat([{
        name: SERIES_TRAILING,
        type: 'areaspline',
        yAxis: 0,
        data: extendedTrailingData.map((value, index) => (index === projectedIndex && value !== null
          ? { y: value, marker: { symbol: 'diamond', radius: 5, fillColor: FORECAST_ORANGE, lineColor: FORECAST_ORANGE } }
          : value)),
        color: NAVY,
        fillOpacity: 0.08,
        lineWidth: 2.5,
        zIndex: 2,
        zoneAxis: 'x',
        // Everything up to the last closed month is actual; the projected tail dashes.
        zones: projectedIndex < 1 ? [] : [{ value: projectedIndex - 1 }, { dashStyle: 'ShortDash', color: FORECAST_ORANGE }],
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 3.5,
          fillColor: '#fff',
          lineColor: NAVY,
          lineWidth: 2
        }
      }]),
        tooltip: {
        shared: true,
        crosshairs: true,
        formatter: function(this: HCContext) {
          // Resolve by point position: labels can repeat, YYYY-MM keys cannot.
          const pointIndex = (this.points[0] as { point?: { index?: number } }).point?.index;
          const category = String(this.points[0]!.category);
          const tooltipLines = [`<b>${(typeof pointIndex === 'number' && extendedMonths[pointIndex]) || category}</b>`];

          // Find current year data
          const currentYearPoint = this.points.find(p => p.series.name === SERIES_CURRENT);
          if (currentYearPoint && currentYearPoint.y !== null) {
            tooltipLines.push(`Current Year: ${formatCurrency(currentYearPoint.y)}`);
          }

          // Find previous year data
          const previousYearPoint = this.points.find(p => p.series.name === SERIES_PREVIOUS);
          if (previousYearPoint && previousYearPoint.y !== null) {
            tooltipLines.push(`Previous Year: ${formatCurrency(previousYearPoint.y)}`);
          }

          // Year-over-year change
          const yoy = (current: number) => {
            const prev = previousYearPoint?.y;
            if (!prev) return null;
            const pct = Math.round(((current - prev) / prev) * 100);
            return `<span style="color: ${pct >= 0 ? '#2e7d32' : '#c62828'};">${pct >= 0 ? '▲ up' : '▼ down'} ${Math.abs(pct)}% vs previous year</span>`;
          };

          if (currentYearPoint && currentYearPoint.y !== null) {
            const line = yoy(currentYearPoint.y);
            if (line) tooltipLines.push(line);
          }

          // Trailing 12-month total at this point
          const trailingPoint = this.points.find(p => p.series.name === SERIES_TRAILING);
          if (trailingPoint && trailingPoint.y !== null) {
            const isProjected = pointIndex === projectedIndex;
            const color = isProjected ? '#f57c00' : NAVY;
            tooltipLines.push(`<span style="color: ${color};">Trailing 12 mo.: <b>${formatCurrency(trailingPoint.y)}</b>${isProjected ? ' <i>(projected)</i>' : ''}</span>`);
          }

          // Find forecast data
          const forecastPoint = this.points.find(p => p.series.name === SERIES_FORECAST);
          if (forecastPoint && forecastPoint.y !== null && forecastTotal !== null) {
            tooltipLines.push(`<span style="color: #f57c00;"><b>Projected Revenue: ${formatCurrency(forecastTotal)}</b></span>`);
            const line = yoy(forecastTotal);
            if (line) tooltipLines.push(`<span style="color: #f57c00;">Projected </span>${line}`);

            // Add progress information
            const now = new Date();
            const currentDay = now.getDate();
            const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            const progress = Math.round((currentDay / daysInMonth) * 100);
            tooltipLines.push(`<i>Based on recent trend and ${progress}% of month completed</i>`);
          }

          return tooltipLines.join('<br/>');
        }
      }
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Trailing 12-Month Revenue, With Month-by-Month Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Navy line: every point is a full year of revenue. Gold bars: each month against the same
              month last year{forecast ? ' • includes current month forecast' : ''}.
            </Typography>
          </Box>
          {trailingNow !== null && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY }}>
                {formatShortCurrency(trailingNow)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                through {latestClosedMonth}
              </Typography>
              {trailingPct !== null && (
                <Typography
                  variant="caption"
                  sx={{ color: trailingPct >= 0 ? 'success.main' : 'error.main' }}
                >
                  {trailingPct >= 0 ? '+' : ''}{trailingPct}% vs prior 12 mo.
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {data?.ordersStatisticsLineChartData && (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
          />
        )}
      </Box>
    );
  };

  const bestSellersPieChartByCollections = () => {
    if (!bestSellersRaw || bestSellersRaw.length === 0) return null;

    const bestSellersPieChartData = bestSellersRaw;

    const orderCountsOptions = {
      chart: {
        type: 'pie'
      },
      credits: {
        enabled: false
      },
      title: {
        text: null
      },
      plotOptions: {
        pie: {
          innerSize: '60%',
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            formatter: function(this: HCContext) {
              return `<b>${this.point.name}</b><br>${formatShortNumber(this.y)} orders`;
            },
            style: {
              textOutline: 'none',
              fontWeight: 'normal',
              color: '#666'
            },
            distance: 20
          }
        }
      },
      series: [{
        name: 'Order Counts',
        data: bestSellersPieChartData
          .map((item) => ({
            name: item.label,
            y: item.amounts
          }))
          .sort((a, b) => b.y - a.y)
      }],
      tooltip: {
        pointFormatter: function(this: HCContext) {
          return `${this.y} order counts`;
        }
      }
    };

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Best Sellers
        </Typography>
        <Box>
          <Typography variant="body2">
            units sold by collection
          </Typography>
          <ToggleButtonGroup
            value={rangeDays}
            exclusive
            size="small"
            onChange={(_, val) => val && setRangeDays(val)}
            sx={{ mb: 1 }}
          >
            <ToggleButton value={30}>Month (30d)</ToggleButton>
            <ToggleButton value={90}>Season (90d)</ToggleButton>
            <ToggleButton value={365}>Year (365d)</ToggleButton>
          </ToggleButtonGroup>
          <HighchartsReact
            highcharts={Highcharts}
            options={orderCountsOptions}
          />
          <Divider textAlign="left">
            <Chip label="By ordered items" size="small" />
          </Divider>
        </Box>
      </Box>
    );
  };

  const topCustomers = () => {
    if (!topCustomersRaw) return null;

    const topCustomersData: Array<{ id: number | string; value: number; name: string; label: string }> = [];
    // bucket by the market's calendar day, matching the server analytics APIs
    const todayStart = getDateOnly(undefined, marketTimeZone);
    const nowMs = todayStart.getTime();
    const fromMs = addDaysInTZ(marketTimeZone, todayStart, -(rangeDays - 1)).getTime();

    topCustomersRaw.forEach((order) => {
      const existingCustomer = topCustomersData.find((customer) => customer.label === `${order.createdAsUserName}#${order.createdAsUserCode}`);
      if (existingCustomer) {
        existingCustomer.value += order.totalPrice;
      } else {
        topCustomersData.push({
          id: order.createdAsUserId,
          value: order.totalPrice,
          name: order.createdAsUserName,
          label: `${order.createdAsUserName}#${order.createdAsUserCode}`,
        });
      }
    });

    const sortedTopCustomers = topCustomersData
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const options = {
      chart: {
        type: 'bar',
        height: 420
      },
      credits: {
        enabled: false
      },
      title: {
        text: null
      },
      xAxis: {
        categories: sortedTopCustomers.map(customer => customer.name),
        labels: {
          useHTML: true,
          formatter: function(this: HCContext) {
            const customer = sortedTopCustomers[this.pos]!;
            const url = `/management/customer/report?customerId=${encodeURIComponent(customer.id)}&dateFrom=${fromMs}&dateTo=${nowMs}`;
            return `<a href="${url}" title="${escHtml(customer.label)}" style="display:block;max-width:130px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;color:inherit;text-decoration:none;cursor:pointer">${escHtml(this.value)}</a>`;
          }
        }
      },
      yAxis: {
        title: {
          text: 'Total Spend'
        },
        labels: {
          formatter: function(this: HCContext) {
            return formatShortCurrency(this.value);
          }
        }
      },
      plotOptions: {
        series: {
          pointPadding: 0,
          groupPadding: 0,
          borderWidth: 0
        },
        bar: {
          pointWidth: 16,
          borderRadius: 3,
          dataLabels: {
            enabled: true,
            formatter: function(this: HCContext) {
              return formatShortCurrency(this.y);
            },
            style: {
              textOutline: 'none',
              fontWeight: 'normal',
              color: '#666'
            }
          }
        }
      },
      legend: {
        enabled: false
      },
      series: [{
        name: 'Total Spend',
        data: sortedTopCustomers.map(customer => customer.value),
        color: '#33688f'
      }],
      tooltip: {
        formatter: function(this: HCContext) {
          const customer = sortedTopCustomers[this.index]!;
          return `${customer.label}: ${formatCurrency(this.y)}`;
        }
      }
    };

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Top 10 Customers
        </Typography>
        <Box>
          <ToggleButtonGroup
            value={rangeDays}
            exclusive
            size="small"
            onChange={(_, val) => val && setRangeDays(val)}
            sx={{ mb: 1 }}
          >
            <ToggleButton value={30}>Month (30d)</ToggleButton>
            <ToggleButton value={90}>Season (90d)</ToggleButton>
            <ToggleButton value={365}>Year (365d)</ToggleButton>
          </ToggleButtonGroup>
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
          />
        </Box>
      </Box>
    );
  };

  const trendingProducts = () => {
    if (!trendingProductsRaw) return null;

    const items = trendingProductsRaw.products;
    // A stored exclusion can name a collection with no sales in the current
    // window; keep it in the options so MUI still matches the chip to an option.
    const pickerOptions = [...new Set([
      ...trendingProductsRaw.collections,
      ...excludedCollections,
    ])].sort();

    // Current period (dark steel blue) vs prior period (muted gray).
    const CURRENT_COLOR = '#33688f';
    const PRIOR_COLOR = '#c4cad0';

    const growthLabel = (product: TrendingProduct) => (
      product.isNew ? 'NEW' : `▲ ${product.growthPct} %`
    );

    const options = {
      chart: {
        type: 'bar',
        // Two bars plus padding per product, so rows stay readable at any count.
        height: 80 + items.length * 46,
      },
      credits: {
        enabled: false
      },
      title: {
        text: null
      },
      xAxis: {
        categories: items.map(product => product.name),
        labels: {
          // Highcharts has no click hook on axis labels, so the label is a real
          // anchor (middle-click / open-in-new-tab keep working) and the click
          // is caught by the delegating wrapper below to route through the SPA.
          useHTML: true,
          formatter: function(this: HCContext) {
            const product = items[this.pos]!;
            const url = `/shop/add?productId=${encodeURIComponent(product.productId)}`;
            return `<a href="${url}" title="${escHtml(this.value)}" style="display:block;max-width:150px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;font-weight:600;color:inherit;text-decoration:none;cursor:pointer">${escHtml(this.value)}</a>`;
          }
        }
      },
      yAxis: {
        title: {
          text: 'Units sold'
        },
        labels: {
          formatter: function(this: HCContext) {
            return formatShortNumber(this.value);
          }
        }
      },
      plotOptions: {
        series: {
          borderWidth: 0,
          groupPadding: 0.12,
          pointPadding: 0.02,
          cursor: 'pointer',
          point: {
            events: {
              click: function(this: HCContext) {
                const product = items[this.index];
                if (product) void navigate(`/shop/add?productId=${product.productId}`);
              }
            }
          }
        },
        bar: {
          borderRadius: 3
        }
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom'
      },
      series: [
        {
          name: 'Current period',
          color: CURRENT_COLOR,
          data: items.map(product => product.currentUnits),
          dataLabels: {
            enabled: true,
            formatter: function(this: HCContext) {
              const product = items[this.point.index as number]!;
              return `${this.y}  <span style="color:${product.isNew ? '#9a7b3e' : '#3c7d46'}">${growthLabel(product)}</span>`;
            },
            useHTML: true,
            style: {
              textOutline: 'none',
              fontWeight: 'normal',
              color: '#666'
            }
          }
        },
        {
          name: 'Previous period',
          color: PRIOR_COLOR,
          data: items.map(product => product.priorUnits),
          dataLabels: {
            enabled: true,
            style: {
              textOutline: 'none',
              fontWeight: 'normal',
              color: '#999'
            }
          }
        }
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function(this: HCContext) {
          const product = items[this.points[0]!.point.index]!;
          const rows = this.points
            .map(point => `${point.series.name}: <b>${point.y}</b> units`)
            .join('<br/>');
          return `<b>${escHtml(product.name)}</b><br/>${rows}<br/>${growthLabel(product)}`;
        }
      }
    };

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Trending Products
        </Typography>
        <Box>
          <Typography variant="body2">
            units sold (current) vs previous period
          </Typography>
          <ToggleButtonGroup
            value={rangeDays}
            exclusive
            size="small"
            onChange={(_, val) => val && setRangeDays(val)}
            sx={{ mb: 1 }}
          >
            <ToggleButton value={30}>Month (30d)</ToggleButton>
            <ToggleButton value={90}>Season (90d)</ToggleButton>
            <ToggleButton value={365}>Year (365d)</ToggleButton>
          </ToggleButtonGroup>
          <Autocomplete
            multiple
            size="small"
            options={pickerOptions}
            value={excludedCollections}
            onChange={(_, values) => {
              // Sorted so ['A','B'] and ['B','A'] hit the same RTK Query cache entry.
              const next = [...values].sort();
              setExcludedCollections(next);
              writeExcludedCollections(next);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Exclude collections"
                placeholder={excludedCollections.length ? '' : 'None excluded'}
              />
            )}
            sx={{ maxWidth: 520, mb: 2 }}
          />
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
              No trending products for this period.
            </Typography>
          ) : (
            <Box
              onClick={(event) => {
                // Modified clicks (new tab / new window / download) and anything
                // but the primary button stay with the browser; only a plain
                // left click is routed through the SPA.
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
                  || event.button !== 0) return;
                const href = (event.target as HTMLElement).closest('a')?.getAttribute('href');
                if (!href) return;
                event.preventDefault();
                void navigate(href);
              }}
            >
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <ConfettiAnimation />
      <Typography variant="h5" gutterBottom>
        Warehouse Analytics Dashboard
      </Typography>
      {data && data.ordersData && data.ordersStatisticsLineChartData && data.comparisonStats && (
        <>
          {cardsGallery()}
          <Box
            sx={{
              display: "flex",
              gap: 2
            }} />
        </>
      )}
      {data && data.ordersStatisticsLineChartData.length > 0 && (
        <>
          {lineChartRevenue()}
          <Box
            sx={{
              display: "flex",
              gap: 2
            }} />
        </>
      )}
      {bestSellersPieChartByCollections()}
      <Box
        sx={{
          display: "flex",
          gap: 2
        }} />
      {trendingProducts()}
      <Box
        sx={{
          display: "flex",
          gap: 2
        }} />
      {topCustomers()}
    </>
  );
}
